import { content, loading, linkPreview } from '../state'
import { postsService } from '../../../services/posts/posts-service'
import { contentService } from '../../../services'
import { urlUtils } from '../../../utils'

/**
 * Fetch post content by ID
 * @param {string|number} postId - ID of the post to fetch
 * @param {Function} emitFn - Function to emit updates to parent component
 * @returns {Promise<Object|null>} The fetched content or null
 */
export const fetchContent = async (postId, emitFn) => {
  // Always start with setting loading to true
  loading.value = true
  
  // Logging removed
  
  // Ensure postId is a string or number, not an object
  if (postId === null || postId === undefined) {
    console.error('fetchContent received null or undefined postId')
    loading.value = false
    content.value = null
    return null
  }
  
  // Handle Vue Ref objects
  if (typeof postId === 'object') {
    if (postId.__v_isRef) {
      // Get the value from the ref
      postId = postId.value
    } else {
      postId = postId?.id || postId?.postId
    }
  }
  
  if (!postId) {
    console.error('Invalid postId after processing:', postId)
    loading.value = false
    content.value = null
    return null
  }
  
  try {
    // Logging removed
    const postData = await postsService.fetchPost(postId)
    // Logging removed
    
    if (!postData) {
      // Logging removed
      content.value = null
      loading.value = false
      return null
    }
    
    // Create a new object to ensure reactivity
    content.value = { ...postData }

    // Process platform info
    if (content.value && typeof content.value.platforms === 'string') {
      try {
        content.value.platforms = JSON.parse(content.value.platforms)
      } catch (e) {
        console.error('Error parsing platforms', e)
        content.value.platforms = []
      }
    }

    content.value.isPublished = content.value.isPublished === 2
    if (typeof emitFn === 'function') {
      emitFn('update:isPublished', content.value.isPublished)
    }

    if (content.value?.link) {
      // Check if link is an image URL
      const isImageUrl = urlUtils.isImageUrl(content.value.link)
      if (!isImageUrl) {
        await fetchLinkPreview(content.value.link)
      }
    }
    
    // Logging removed
    return content.value
  } catch (error) {
    console.error('Error loading post content:', error)
    content.value = null  // Set content to null on error
    return null
  } finally {
    // Always make sure loading is set to false at the end
    loading.value = false
    // Logging removed
  }
}

/**
 * Fetch preview for a link
 * @param {string} url - URL to fetch preview for
 */
export const fetchLinkPreview = async (url) => {
  // Check if URL is an image
  if (urlUtils.isImageUrl(url)) return

  try {
    const previewData = await contentService.getLinkPreview(url)

    if (previewData) {
      linkPreview.value = {
        title: previewData.title || urlUtils.extractDomain(url),
        description: previewData.description || '',
        image: previewData.image || '',
      }
    }
  } catch (error) {
    console.error('Error fetching link preview', error)
    linkPreview.value = {
      title: urlUtils.extractDomain(url),
      description: '',
      image: '',
    }
  }
}